import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap, take } from 'rxjs/operators';
import { LoginResponse } from 'angular-auth-oidc-client';
import { ConfigService } from '@shared/services/config.service';
import { AuthService } from '@shared/auth/auth.service';
import { SessionService } from '@services/session.service';

@Injectable({
  providedIn: 'root',
})
export class HttpHelperService {
  constructor(
    private readonly http: HttpClient,
    private readonly config: ConfigService,
    private readonly authService: AuthService,
    private readonly sessionService: SessionService,
  ) {}

  private baseHeaders(contentType = 'application/json'): any {
    return {
      'Content-Type': contentType,
      'Accept': contentType,
      'X-NW-Message-ID': crypto.randomUUID(),
      'client_id': this.config.app.clientId,
      'target-env': this.config.app.awsTargetEnv,
      'X-API-Key': this.config.app.awsApiKey,
      'full-story-session-id': this.sessionService.retrieveCurrentFullStorySessionId(),
      'Session-Id': this.sessionService.retrieveCurrentSessionId(),
    };
  }

  private additionalHeaders(requiresAuth = true): Observable<any | {}> {
    if (!requiresAuth) return of({});
    return this.authService.pingAccessToken$.pipe(
      take(1), // will prevent looping calls to the authService.pingAccessToken$ observable
      map((accessToken: string) => ({ Authorization: `Bearer ${accessToken}` })),
    );
  }

  // If sending in "baseOptions.headers", values must be sent as objects like the "baseHeaders" returns above, not as HttpHeader Types.
  private determineOptions(requiresAuth = true, contentType = '', baseOptions: any = {}): Observable<any> {
    return this.additionalHeaders(requiresAuth).pipe(
      map((addlHeaders: any | {}) => {
        const options = { ...(baseOptions ?? {}) };
        options.headers = new HttpHeaders({ ...this.baseHeaders(contentType), ...addlHeaders, ...options?.headers });
        return options;
      }),
    );
  }

  handleErrorResponse = (retry: any) =>
    catchError((err) => {
      if (err.status === 401) {
        console.log('Error in #handleErrorResponse:', err);
        return from(this.authService.forceRefreshSession()).pipe(
          switchMap((loginResponse: LoginResponse): any => (loginResponse.accessToken ? retry : throwError(() => err))),
        );
      } else {
        return throwError(() => err);
      }
    });

  private derivePath(path: string, options: any = {}): string {
    return options?.usePathAsUrl ? path : this.config.app.api + path;
  }

  get<T = any>(path: string, contentType = 'application/json', baseOptions: any = {}, requiresAuth = true): Observable<T> {
    return this.determineOptions(requiresAuth, contentType, baseOptions).pipe(
      mergeMap((options: any) => {
        const derivedPath = this.derivePath(path, baseOptions);
        return this.http.get<T>(derivedPath, { observe: 'body', headers: options.headers, responseType: options.responseType });
      }),
      retry({
        delay: (err) => {
          if (err.status === 401 || err.status === 403) {
            return this.authService.forceRefreshSession();
          } else {
            throw err;
          }
        },
        count: 1,
      }),
    );
  }

  getBinary(path: string, baseOptions: any = {}, requiresAuth = true): Observable<Blob> {
    return this.determineOptions(requiresAuth, '', baseOptions).pipe(
      mergeMap((options: any) => {
        const derivedPath = this.derivePath(path, baseOptions);
        return this.http.get(derivedPath, {
          observe: 'body',
          headers: options.headers,
          responseType: 'blob', // Explicitly set responseType to 'blob'
        }) as Observable<Blob>;
      }),
      catchError((err) => {
        console.error('Error fetching binary data:', err);
        return throwError(() => err);
      }),
    );
  }

  post<T = any>(path: string, body: any = null, baseOptions: any = {}, requiresAuth = true): Observable<T> {
    path = this.derivePath(path, baseOptions);
    return this.determineOptions(requiresAuth, 'application/json', baseOptions).pipe(
      switchMap((options: any) => {
        const observable = this.http.post<T>(path, body, options);
        return observable.pipe(this.handleErrorResponse(observable)) as Observable<T>;
      }),
    );
  }

  patch<T = any>(path: string, body: any = null, baseOptions: any = {}, requiresAuth = true): Observable<T> {
    path = this.derivePath(path, baseOptions);
    return this.determineOptions(requiresAuth, 'application/json', baseOptions).pipe(
      switchMap((options: any) => {
        const observable = this.http.patch<T>(path, body, options);
        return observable.pipe(this.handleErrorResponse(observable)) as Observable<T>;
      }),
    );
  }

  put<T = any>(path: string, body: any = null, baseOptions: any = {}, requiresAuth = true): Observable<T> {
    path = this.derivePath(path, baseOptions);
    return this.determineOptions(requiresAuth, 'application/json', baseOptions).pipe(
      switchMap((options: any) => {
        const observable = this.http.put<T>(path, body, options);
        return observable.pipe(this.handleErrorResponse(observable)) as Observable<T>;
      }),
    );
  }

  delete<T = any>(path: string, body: any, baseOptions: any = {}, requiresAuth = true): Observable<T> {
    path = this.derivePath(path, baseOptions);
    return this.determineOptions(requiresAuth, 'application/json', baseOptions).pipe(
      switchMap((options: any) => {
        const observable = this.http.request<T>('delete', path, { ...options, body });
        return observable.pipe(this.handleErrorResponse(observable)) as Observable<T>;
      }),
    );
  }
}
